import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AdjustmentsHorizontalIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';
import { eventsApi } from '../../api/eventsApi';
import './events.css';

const PER_PAGE = 12;
const INITIAL_EVENTS_LIMIT = 45;

const getField = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== '') {
      return item[key];
    }
  }
  return undefined;
};

const normalizeStatus = (status) => String(status || 'UNKNOWN').toUpperCase();

const getStatusClass = (status) => {
  switch (normalizeStatus(status)) {
    case 'ONGOING':
    case 'ACTIVE':
    case 'IN_PROGRESS':
      return 'live';
    case 'COMPLETED':
      return 'completed';
    case 'UPCOMING':
    case 'SCHEDULED':
      return 'neutral';
    case 'CANCELLED':
    case 'ARCHIVED':
      return 'muted';
    default:
      return 'neutral';
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const formatRelative = (value) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diffSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diffSeconds < 60) return 'just now';
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  if (diffSeconds < 86400 * 7) return `${Math.floor(diffSeconds / 86400)}d ago`;
  return formatDate(value);
};

const getEventDate = (event) => getField(event, 'created_at', 'createdAt');
const getEventActivityDate = (event) => getField(event, 'last_activity', 'lastActivity', 'updated_at', 'updatedAt');

const isWithinDateRange = (value, from, to) => {
  if (!from && !to) return true;
  if (!value) return false;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;

  if (from) {
    const fromDate = new Date(`${from}T00:00:00`);
    if (date < fromDate) return false;
  }

  if (to) {
    const toDate = new Date(`${to}T23:59:59.999`);
    if (date > toDate) return false;
  }

  return true;
};

const EventCard = ({ event }) => {
  const eventId = getField(event, 'id');
  const title = getField(event, 'title') || 'Untitled event';
  const description = getField(event, 'description');
  const statusValue = getField(event, 'status');
  const status = normalizeStatus(statusValue);
  const organization = getField(event, 'organization', 'org');
  const sessionsCountValue = getField(event, 'session_count', 'sessionCount', 'sessions_count', 'sessionsCount');
  const createdAt = getEventDate(event);
  const lastActivity = getEventActivityDate(event);
  const cardStats = [
    sessionsCountValue !== undefined && {
      value: Number(sessionsCountValue),
      label: Number(sessionsCountValue) === 1 ? 'session' : 'sessions',
    },
    createdAt && {
      value: formatDate(createdAt),
      label: 'created',
    },
    lastActivity && {
      value: formatRelative(lastActivity),
      label: 'last activity',
    },
  ].filter(Boolean);

  return (
    <Link to={`/events/${eventId}`} className="events-card">
      {(statusValue || organization) && (
        <div className="events-card-top">
          {statusValue && (
            <span className={`events-badge events-badge-${getStatusClass(status)}`}>
              <span className="events-badge-dot" />
              {status.replaceAll('_', ' ')}
            </span>
          )}
          {organization && <span className="events-card-org">{organization}</span>}
        </div>
      )}

      <h3 className="events-card-title">{title}</h3>
      {description && <p className="events-card-desc">{description}</p>}

      {cardStats.length > 0 && (
        <div className="events-card-foot">
          {cardStats.map((stat) => (
            <div key={stat.label} className="events-card-stat">
              <span>{stat.value}</span>
              <small>{stat.label}</small>
            </div>
          ))}
        </div>
      )}
    </Link>
  );
};

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState('newest');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    const fetchEvents = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await eventsApi.getAll({ skip: 0, limit: INITIAL_EVENTS_LIMIT });
        setEvents(Array.isArray(response?.events)
          ? response.events
          : (Array.isArray(response?.results) ? response.results : []));
      } catch (err) {
        console.error('Failed to fetch events:', err);
        setError('Failed to load events. Please try again later.');
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [user]);

  const statusOptions = useMemo(() => {
    const statuses = events
      .map(event => normalizeStatus(getField(event, 'status')))
      .filter(value => value && value !== 'UNKNOWN')
      .filter((value, index, arr) => arr.indexOf(value) === index)
      .sort();

    return ['all', ...statuses];
  }, [events]);

  const filteredEvents = useMemo(() => {
    const q = query.trim().toLowerCase();
    const selectedStatus = status.toUpperCase();

    const filtered = events.filter(event => {
      const title = String(getField(event, 'title') || '').toLowerCase();
      const description = String(getField(event, 'description') || '').toLowerCase();
      const organization = String(getField(event, 'organization', 'org') || '').toLowerCase();
      const eventStatus = normalizeStatus(getField(event, 'status'));

      if (selectedStatus !== 'ALL' && eventStatus !== selectedStatus) return false;
      if (!isWithinDateRange(getEventDate(event), dateFrom, dateTo)) return false;
      if (!q) return true;

      return title.includes(q) || description.includes(q) || organization.includes(q);
    });

    filtered.sort((a, b) => {
      if (sort === 'oldest') return new Date(getEventDate(a) || 0) - new Date(getEventDate(b) || 0);
      if (sort === 'az') return String(getField(a, 'title') || '').localeCompare(String(getField(b, 'title') || ''));
      if (sort === 'sessions') {
        return Number(getField(b, 'session_count', 'sessionCount', 'sessions_count', 'sessionsCount') || 0) -
          Number(getField(a, 'session_count', 'sessionCount', 'sessions_count', 'sessionsCount') || 0);
      }
      return new Date(getEventDate(b) || 0) - new Date(getEventDate(a) || 0);
    });

    return filtered;
  }, [dateFrom, dateTo, events, query, sort, status]);

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filteredEvents.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  const activeFilterCount = [status !== 'all', Boolean(dateFrom), Boolean(dateTo)].filter(Boolean).length;

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, query, status, sort]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const goToPage = (nextPage) => {
    const parsedPage = Number(nextPage);
    if (!Number.isFinite(parsedPage)) return;
    const safePage = Math.min(Math.max(Math.trunc(parsedPage), 1), totalPages);
    setPage(safePage);
  };

  const handlePageSubmit = (event) => {
    event.preventDefault();
    goToPage(pageInput);
  };

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const handleClick = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [filtersOpen]);

  const resetFilters = () => {
    setStatus('all');
    setDateFrom('');
    setDateTo('');
  };

  if (!user) {
    return <LoginForm />;
  }

  return (
    <div className="events-page">
      <div className="events-content">
        <div className="events-page-head">
          <div>
            <h1>Events</h1>
            <p>{filteredEvents.length} of {events.length} events</p>
          </div>
        </div>

        <div className="events-toolbar">
          <div className="events-input events-search">
            <MagnifyingGlassIcon />
            <input
              placeholder="Search events..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <div className="events-toolbar-spacer" />

          <div className="events-filter-wrap" ref={popoverRef}>
            <button
              type="button"
              className={`events-btn ${activeFilterCount ? 'active' : ''}`}
              onClick={() => setFiltersOpen(open => !open)}
            >
              <AdjustmentsHorizontalIcon />
              Filters
              {activeFilterCount > 0 && <span className="events-filter-count">{activeFilterCount}</span>}
            </button>

            {filtersOpen && (
              <div className="events-popover">
                <div className="events-popover-head">
                  <strong>Filters</strong>
                  {activeFilterCount > 0 && <button type="button" onClick={resetFilters}>Reset</button>}
                </div>

                <label>
                  <span>Status</span>
                  <select value={status} onChange={(event) => setStatus(event.target.value)}>
                    {statusOptions.map(option => (
                      <option key={option} value={option}>
                        {option === 'all' ? 'Any status' : option.replaceAll('_', ' ')}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="events-date-field">
                  <span>Date from</span>
                  <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
                </label>

                <label className="events-date-field">
                  <span>Date to</span>
                  <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
                </label>
              </div>
            )}
          </div>

          <select className="events-input" value={sort} onChange={(event) => setSort(event.target.value)}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="az">A to Z</option>
            <option value="sessions">Most sessions</option>
          </select>
        </div>

        {loading ? (
          <div className="events-empty events-card-shell">
            <div className="events-spinner" />
            <div>
              <strong>Loading events...</strong>
              <span>Fetching event list from the backend.</span>
            </div>
          </div>
        ) : error ? (
          <div className="events-empty events-card-shell events-error">
            <CalendarDaysIcon />
            <div>
              <strong>Events unavailable</strong>
              <span>{error}</span>
            </div>
          </div>
        ) : pageItems.length === 0 ? (
          <div className="events-empty events-card-shell">
            <CalendarDaysIcon />
            <div>
              <strong>No events match.</strong>
              <span>Clear search or try a different status.</span>
            </div>
          </div>
        ) : (
          <div className="events-grid">
            {pageItems.map(event => (
              <EventCard key={getField(event, 'id')} event={event} />
            ))}
          </div>
        )}

        {!loading && !error && pageItems.length > 0 && (
          <div className="events-pagination">
            <span>{(currentPage - 1) * PER_PAGE + 1}-{Math.min(currentPage * PER_PAGE, filteredEvents.length)} of {filteredEvents.length}</span>
            <div className="events-pagination-controls">
              <button disabled={currentPage === 1} onClick={() => goToPage(1)}>
                First
              </button>
              <button disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)} aria-label="Previous page">
                <ChevronLeftIcon />
              </button>

              <form className="events-page-jump" onSubmit={handlePageSubmit}>
                <span>Page</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={pageInput}
                  onChange={(event) => setPageInput(event.target.value)}
                  onBlur={() => setPageInput(String(currentPage))}
                />
                <span>of {totalPages}</span>
              </form>

              <button disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)} aria-label="Next page">
                <ChevronRightIcon />
              </button>
              <button disabled={currentPage === totalPages} onClick={() => goToPage(totalPages)}>
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
